import React from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';

const card = {
  background: 'rgba(30,45,79,0.5)',
  borderRadius: '16px',
  border: '1px solid rgba(0,194,168,0.1)',
  padding: '24px',
  marginBottom: '20px',
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1E2D4F', border: '1px solid rgba(0,194,168,0.3)', borderRadius: '8px', padding: '12px 16px' }}>
      <div style={{ fontFamily: 'Space Grotesk', fontSize: '12px', color: '#5B7BA8', marginBottom: '8px' }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ fontFamily: 'JetBrains Mono', fontSize: '13px', color: p.color, display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 600 }}>
            {p.name === 'Pass Rate' ? `${p.value}%` : p.value.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

export function PolicyReadinessBanner({ timeline }) {
  const last4 = (timeline || []).slice(-4);
  if (!last4.length) return null;
  const avgRate = Math.round(
    last4.reduce((s, d) => s + (d.total > 0 ? (d.pass / d.total) * 100 : 0), 0) / last4.length
  );

  let status, color, icon, advice;
  if (avgRate >= 95) {
    status = 'Ready to advance policy';
    color = '#00C2A8';
    icon = '✓';
    advice = `Your 4-week average pass rate is ${avgRate}% — above the 95% threshold used by most providers. If your current policy is p=none or p=quarantine, consider tightening it.`;
  } else if (avgRate >= 80) {
    status = 'Making progress';
    color = '#F5A623';
    icon = '◐';
    advice = `4-week average is ${avgRate}%. Identify and fix remaining failing sources before advancing your policy — aim for 95%+ over 30 days.`;
  } else {
    status = 'Needs attention';
    color = '#E8415A';
    icon = '⚠';
    advice = `4-week average is ${avgRate}%. Do not advance your DMARC policy yet — investigate failing source IPs and ensure all legitimate senders are authenticated.`;
  }

  return (
    <div style={{ padding: '16px 20px', borderRadius: '12px', border: `1px solid ${color}30`, background: `${color}0C`, marginBottom: '20px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
      <div style={{ fontSize: '20px', color, lineHeight: 1, paddingTop: '2px' }}>{icon}</div>
      <div>
        <div style={{ fontFamily: 'Space Grotesk', fontSize: '14px', fontWeight: 600, color, marginBottom: '4px' }}>
          Policy Readiness: {status}
        </div>
        <div style={{ fontFamily: 'Inter', fontSize: '13px', color: '#7B9CC8', lineHeight: 1.55 }}>{advice}</div>
      </div>
    </div>
  );
}

export default function TrendChart({ data }) {
  if (!data || data.length === 0) return null;

  const formatted = data.map(d => ({
    week: d.week,
    Pass: d.pass,
    Fail: d.total - d.pass,
    'Pass Rate': d.total > 0 ? Math.round((d.pass / d.total) * 100) : 0,
  }));

  return (
    <div style={card}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: '16px', color: '#F0F4FF' }}>
            Authentication Trend
          </div>
          <div style={{ fontFamily: 'Inter', fontSize: '12px', color: '#5B7BA8', marginTop: '4px' }}>
            Weekly pass / fail volume with pass rate % — dashed line marks the 95% enforcement threshold
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={formatted} margin={{ top: 5, right: 44, bottom: 5, left: 0 }}>
          <defs>
            <linearGradient id="passBarG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00C2A8" stopOpacity={0.85} />
              <stop offset="100%" stopColor="#00C2A8" stopOpacity={0.45} />
            </linearGradient>
            <linearGradient id="failBarG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#E8415A" stopOpacity={0.85} />
              <stop offset="100%" stopColor="#E8415A" stopOpacity={0.45} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,45,79,0.9)" />
          <XAxis dataKey="week" tick={{ fontFamily: 'JetBrains Mono', fontSize: 10, fill: '#5B7BA8' }} />
          <YAxis yAxisId="vol" tick={{ fontFamily: 'JetBrains Mono', fontSize: 10, fill: '#5B7BA8' }} />
          <YAxis
            yAxisId="rate"
            orientation="right"
            domain={[0, 100]}
            tick={{ fontFamily: 'JetBrains Mono', fontSize: 10, fill: '#5B7BA8' }}
            tickFormatter={v => `${v}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontFamily: 'Space Grotesk', fontSize: '13px' }} />
          <Bar yAxisId="vol" dataKey="Pass" stackId="s" fill="url(#passBarG)" />
          <Bar yAxisId="vol" dataKey="Fail" stackId="s" fill="url(#failBarG)" radius={[3, 3, 0, 0]} />
          <ReferenceLine
            yAxisId="rate"
            y={95}
            stroke="#F5A623"
            strokeDasharray="6 3"
            label={{ value: '95% target', position: 'insideTopRight', fill: '#F5A623', fontFamily: 'Space Grotesk', fontSize: 10, dy: -4 }}
          />
          <Line
            yAxisId="rate"
            type="monotone"
            dataKey="Pass Rate"
            stroke="#F0F4FF"
            strokeWidth={2.5}
            dot={{ fill: '#F0F4FF', r: 3, strokeWidth: 0 }}
            activeDot={{ r: 5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
